import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db } from '../../firebase';
import { collection, getDocs, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../../services/firestoreService';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanyId } from '../../hooks/useCompanyId';
import { getUserTechStackData, updateUserTechStack } from '../../services/userTechnologyService';
import { FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import DataDebugger from '../../components/DataDebugger';
import StartSidebar from '../../components/StartSidebar';
import PortalBackground from '../../components/PortalBackground';
import { resolveCoreToolLinks, selectionDisplayName } from '../../utils/techStackCoreLinks';
import { DEFAULT_TIER_ORDER, applyTierMeta, compareTechSoftware, compareTechTypes, fetchTechTierMeta } from '../../utils/techStackTiers';

const CRMtechstack = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { companyId, loading: companyIdLoading } = useCompanyId();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [tierOrder, setTierOrder] = useState(DEFAULT_TIER_ORDER);
  const [showPercentages, setShowPercentages] = useState(false);
  const [showDetailedCosts, setShowDetailedCosts] = useState(false);
  const [userTechStack, setUserTechStack] = useState([]);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [activeTool, setActiveTool] = useState(null);
  const [activeTab, setActiveTab] = useState('select');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userCount, setUserCount] = useState(1);
  const [customCRMName, setCustomCRMName] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // Define the tech stack structure with empty initial state
  const [techStack, setTechStack] = useState({
    core: {
      id: 'core',
      title: 'Core',
      color: 'bg-red-700',
      width: '40%',
      tools: []
    },
    primary: {
      id: 'primary',
      title: 'Primary',
      color: 'bg-red-600',
      width: '70%',
      tools: []
    },
    secondary: {
      id: 'secondary',
      title: 'Secondary',
      color: 'bg-red-500',
      width: '100%',
      tools: []
    }
  });

  // Fetch data from Firebase with real-time updates
  // isStale lets an unmounted or superseded load (e.g. companyId resolved
  // mid-flight) drop its result instead of overwriting fresher state
  const fetchTechStackData = async (userData = null, isStale = () => false) => {
    try {
      const collectionNames = ['Core', 'Primary', 'Secondary'];
      const tierMeta = await fetchTechTierMeta();
      let newTechStack = { ...techStack };
      // Core tool types by id, so Primary/Secondary types can offer "your <Core tool>"
      const coreTypesById = new Map();
      
      // Use provided user data or current userTechStack state
      const currentUserData = userData || userTechStack;
      console.log('fetchTechStackData - currentUserData:', currentUserData);

      for (const collectionName of collectionNames) {
        const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
        const tools = [];

        for (const typeDoc of typesSnapshot.docs) {
          const typeData = typeDoc.data();
          
          const softwareSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares'));
          const software = [];

          softwareSnapshot.forEach(softwareDoc => {
            const data = softwareDoc.data();
          
            if (data && data.name) {
              // Convert old useCases format to new useCaseIds format
              const useCaseIds = data.useCaseIds || (data.useCases ? data.useCases.map((_, index) => index.toString()) : []);
              
              software.push({
                id: softwareDoc.id,
                name: data.name,
                description: data.description || '',
                bbaOpinion: data.bbaOpinion || '',
                useCaseIds: useCaseIds,
                pricing: data.pricing || [],
                image: data.image || '',
                suggested: data.suggested || false,
                open: data.open || false,
                order: data.order
              });
            }
          });

          if (collectionName === 'Core') {
            coreTypesById.set(typeDoc.id, { name: typeData.name, software });
          } else {
            software.push(...resolveCoreToolLinks(typeData, coreTypesById, currentUserData, software));
          }

          // Template order if set, else suggested first
          software.sort(compareTechSoftware);

          if (typeData && typeData.name) {
            // Check if user has a saved selection for this tool
            const savedSelection = currentUserData.find(item => 
              item.categoryId === collectionName.toLowerCase() && item.toolId === typeDoc.id
            );
            
            console.log(`Tool ${typeData.name} (${typeDoc.id}) - savedSelection:`, savedSelection);
            
            tools.push({
              id: typeDoc.id,
              name: typeData.name,
              order: typeData.order,
              software: selectionDisplayName(savedSelection, software), // Saved selection (a linked Core pick follows the Core tool) or empty
              monthlyCost: savedSelection ? (savedSelection.monthlyCost || 0) : 0, // Use saved cost or 0
              annualCost: savedSelection ? (savedSelection.annualCost || 0) : 0, // Use saved cost or 0
              description: typeData.description || '',
              bbaOpinion: '',
              useCaseIds: [],
              useCases: typeData.useCases || [], // Add use cases from type
              availableSoftware: software,
              selectedPlan: savedSelection ? savedSelection.selectedPlan : null, // Preserve selected plan
              userCount: savedSelection ? savedSelection.userCount : 1, // Preserve user count
              customName: savedSelection ? savedSelection.customName : '' // Preserve custom name
            });
          }
        }

        // Convert collection name to lowercase for state key
        const stateKey = collectionName.toLowerCase();
        newTechStack[stateKey].tools = tools.sort(compareTechTypes);
      }
      newTechStack = applyTierMeta(newTechStack, tierMeta);

      console.log('Final tech stack with user selections:', newTechStack);
      if (isStale()) return;
      setTierOrder(tierMeta.map((t) => t.id));
      setTechStack(newTechStack);
      setIsLoaded(true);
    } catch (error) {
      console.error('Error fetching tech stack data:', error);
      if (isStale()) return;
      setIsLoaded(true); // Still set loaded to true to show UI even if there's an error
    }
  };

  // Set up real-time listener and fetch data
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Wait for the company lookup to settle: loading with companyId=null would
    // read (and later save) the per-user fallback doc for a company user
    if (!currentUser || companyIdLoading) return;

    let cancelled = false;
    const isStale = () => cancelled;

    // Fetch user & company data first, then tech stack data
    const loadData = async () => {
      try {
        // Load user document to get saved assessment flags/layout
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        let savedTechStackData = null;
        let techStackCompleted = false;

        if (userDoc.exists()) {
          const userData = userDoc.data();

          if (userData.businessAssessment?.techStackCompleted || userData.techData?.techStackCompleted) {
            techStackCompleted = true;
            savedTechStackData =
              userData.businessAssessment?.techStackData || userData.techData?.techStackData || null;
          }
        }

        // Load selections from shared tech stack (company-wide when companyId exists)
        const userTechData = await getUserTechStackData(currentUser.uid, companyId || null);
        const userSelections = userTechData.selectedSoftware || [];
        console.log('Loaded tech stack selections:', userSelections);
        if (cancelled) return;
        setUserTechStack(userSelections);

        if (techStackCompleted && savedTechStackData) {
          console.log('Tech stack already completed, loading saved data');
          setTechStack(savedTechStackData);
          setIsLoaded(true);
          return;
        }

        // Load tech stack data with selections preserved
        await fetchTechStackData(userSelections, isStale);
      } catch (error) {
        console.error('Error loading tech stack data:', error);
        // Fallback to loading fresh data
        await fetchTechStackData([], isStale);
      }
    };

    // Re-run the full load whenever the stack templates change (the initial
    // snapshot fires immediately, so this also covers first load)
    const unsubscribe = onSnapshot(
      collection(db, 'technologyStack'),
      () => {
        loadData();
      },
      (error) => {
        console.error('Error listening to tech stack changes:', error);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [currentUser, companyId, companyIdLoading]);

  // Calculate total costs and percentages
  const calculateCosts = () => {
    let totalMonthlyAll = 0;
    let componentCosts = {};
    let softwareCount = 0;
    let integratedCount = 0;
    
    // Calculate totals for each component and overall
    Object.entries(techStack).forEach(([categoryId, category]) => {
      let componentMonthly = 0;
      
      category.tools.forEach(tool => {
        const monthlyCost = parseFloat(tool.monthlyCost) || 0;
        componentMonthly += monthlyCost;
        totalMonthlyAll += monthlyCost;
        
        // Count software and integration
        if (tool.software && tool.software !== 'None' && tool.monthlyCost > 0) {
          softwareCount++;
          // Check if the selected software has open API
          const selectedSoftware = tool.availableSoftware?.find(sw => sw.name === tool.software);
          if (selectedSoftware?.open) {
            integratedCount++;
          }
        }
      });
      
      componentCosts[categoryId] = {
        monthly: componentMonthly,
        annual: componentMonthly * 12
      };
    });
    
    // Calculate percentages of total cost
    Object.keys(componentCosts).forEach(categoryId => {
      componentCosts[categoryId].percentOfTotal = totalMonthlyAll > 0 
        ? (componentCosts[categoryId].monthly / totalMonthlyAll) * 100 
        : 0;
    });
    
    // Calculate tool percentages within each component
    Object.entries(techStack).forEach(([categoryId, category]) => {
      const componentMonthly = componentCosts[categoryId].monthly;
      
      if (componentMonthly > 0) {
        category.tools.forEach(tool => {
          const monthlyCost = parseFloat(tool.monthlyCost) || 0;
          tool.percentOfComponent = (monthlyCost / componentMonthly) * 100;
        });
      } else {
        category.tools.forEach(tool => {
          tool.percentOfComponent = 0;
        });
      }
    });
    
    // Calculate integration percentage
    const integrationPercentage = softwareCount > 0 ? Math.round((integratedCount / softwareCount) * 100) : 0;
    
    return {
      totalMonthly: totalMonthlyAll,
      totalAnnual: totalMonthlyAll * 12,
      integrationPercentage,
      softwareCount,
      integratedCount,
      components: componentCosts
    };
  };

  const costs = calculateCosts();

  // Order categories from top to bottom for display (Core at top)
  const orderedCategories = tierOrder;

  // Function to check if a tool is CRM-related
  const isCRMTool = (tool) => {
    // Only allow the actual CRM tool to be clickable, not lead management or other related tools
    return tool.name.toLowerCase().includes('crm') || tool.id === 'crm';
  };

  // Function to handle CRM tool click
  const handleCRMToolClick = (e, tool, categoryId) => {
    e.stopPropagation();
    if (isCRMTool(tool)) {
      setActiveTool(tool.id);
      setActiveTab('select');
      
      // Initialize selected plan and user count if user has saved selections
      if (tool.software && tool.software !== 'None') {
        // Find the selected software to get its ID
        const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
        if (selectedSoftware) {
          // Find the selected plan index
          const planIndex = selectedSoftware.pricing?.findIndex(plan => 
            plan.name === tool.selectedPlan?.name || 
            (plan.monthlyCost === tool.selectedPlan?.monthlyCost && plan.includedUsers === tool.selectedPlan?.includedUsers)
          );
          
          if (planIndex !== -1 && planIndex !== undefined) {
            setSelectedPlan({ 
              softwareId: selectedSoftware.id, 
              planIndex: planIndex, 
              plan: selectedSoftware.pricing[planIndex] 
            });
          }
          
          // Set user count if available
          if (tool.userCount) {
            setUserCount(tool.userCount);
          } else if (tool.selectedPlan?.includedUsers) {
            setUserCount(tool.selectedPlan.includedUsers);
          } else {
            setUserCount(1);
          }
        }
      } else {
        // Reset selections for tools with no software or "None" selected
        setSelectedPlan(null);
        setUserCount(1);
      }
      
      // Initialize custom name if tool has one
      if (tool.customName) {
        setCustomCRMName(tool.customName);
        setShowCustomInput(tool.software === 'Other');
      } else {
        setCustomCRMName('');
        setShowCustomInput(false);
      }
    }
  };

  // Find the active tool's data if a popup is open
  const getActiveTool = () => {
    if (!activeTool) return null;
    
    // Find the tool across all categories
    for (const categoryId of orderedCategories) {
      const toolData = techStack[categoryId].tools.find(tool => tool.id === activeTool);
      if (toolData) {
        return { ...toolData, categoryId: categoryId };
      }
    }
    return null;
  };
  
  const activeToolData = getActiveTool();

  // Handle input changes for software selection
  const handleInputChange = (e, field, toolId, categoryId) => {
    const value = e.target.value;
    
    const updatedTechStack = JSON.parse(JSON.stringify(techStack));
    const toolIndex = updatedTechStack[categoryId].tools.findIndex(tool => tool.id === toolId);
    
    if (toolIndex !== -1) {
      if (field === 'software') {
        if (value === '') {
          // Handle empty selection - clear software and costs
          updatedTechStack[categoryId].tools[toolIndex].software = '';
          updatedTechStack[categoryId].tools[toolIndex].monthlyCost = 0;
          updatedTechStack[categoryId].tools[toolIndex].annualCost = 0;
          
          // Remove from user's tech stack
          if (currentUser) {
            const updatedUserTechStack = userTechStack.filter(item => 
              !(item.categoryId === categoryId && item.toolId === toolId)
            );
            
            setUserTechStack(updatedUserTechStack);
            updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
          }
        } else if (value === 'None') {
          // Handle "None" selection - set software to "None" and clear costs
          updatedTechStack[categoryId].tools[toolIndex].software = 'None';
          updatedTechStack[categoryId].tools[toolIndex].monthlyCost = 0;
          updatedTechStack[categoryId].tools[toolIndex].annualCost = 0;
          
          // Save to user's tech stack
          if (currentUser) {
            const noneSoftwareData = {
              id: 'none',
              name: 'None',
              categoryId,
              toolId,
              monthlyCost: 0,
              annualCost: 0,
              selectedAt: new Date().toISOString()
            };
            
            const updatedUserTechStack = [...userTechStack.filter(item => 
              !(item.categoryId === categoryId && item.toolId === toolId)
            ), noneSoftwareData];
            
            console.log('Saving None selection:', noneSoftwareData);
            setUserTechStack(updatedUserTechStack);
            updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
          }
        } else {
          // Find the selected software from available options
          const selectedSoftware = updatedTechStack[categoryId].tools[toolIndex].availableSoftware.find(
            sw => sw.name === value
          );

          if (selectedSoftware) {
            updatedTechStack[categoryId].tools[toolIndex].software = selectedSoftware.name;
            updatedTechStack[categoryId].tools[toolIndex].useCases = selectedSoftware.useCases || '';
            
            // Update costs if pricing is available
            if (selectedSoftware.pricing && selectedSoftware.pricing[0]) {
              let monthlyCost = 0;
              const plan = selectedSoftware.pricing[0];
              
              if (plan.tieredPricing && plan.tiers && plan.tiers.length > 0) {
                // For tiered pricing, use the base cost from the first tier
                monthlyCost = parseFloat(plan.tiers[0]?.baseMonthlyCost) || 0;
              } else if (plan.variablePricing) {
                // For variable pricing, use the user-entered monthly cost (default to 0)
                monthlyCost = parseFloat(plan.monthlyCost) || 0;
              } else {
                // For simple pricing, use the monthly cost
                monthlyCost = parseFloat(plan.monthlyCost) || 0;
              }
              
              updatedTechStack[categoryId].tools[toolIndex].monthlyCost = monthlyCost;
              updatedTechStack[categoryId].tools[toolIndex].annualCost = monthlyCost * 12;
            }

            // Save to user's tech stack
            if (currentUser) {
              const selectedSoftwareData = {
                ...selectedSoftware,
                categoryId,
                toolId,
                monthlyCost: updatedTechStack[categoryId].tools[toolIndex].monthlyCost,
                annualCost: updatedTechStack[categoryId].tools[toolIndex].annualCost,
                selectedAt: new Date().toISOString()
              };
              
              const updatedUserTechStack = [...userTechStack.filter(item => 
                !(item.categoryId === categoryId && item.toolId === toolId)
              ), selectedSoftwareData];
              
              console.log('Saving software selection:', selectedSoftwareData);
              setUserTechStack(updatedUserTechStack);
              updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
            }
          }
        }
      } else {
        updatedTechStack[categoryId].tools[toolIndex][field] = value;
        
        if (field === 'monthlyCost') {
          updatedTechStack[categoryId].tools[toolIndex].annualCost = (parseFloat(value) || 0) * 12;
        } else if (field === 'annualCost') {
          updatedTechStack[categoryId].tools[toolIndex].monthlyCost = (parseFloat(value) || 0) / 12;
        }
      }
    }
    
    setTechStack(updatedTechStack);
  };

  // Handle closing the popup
  const handleClosePopup = () => {
    setActiveTool(null);
    setSelectedPlan(null);
    setUserCount(1);
    setActiveTab('select');
    setCustomCRMName('');
    setShowCustomInput(false);
  };

  // Function to save CRM selection to leads collection
  const saveCRMSelectionToLead = async (crmSelection) => {
    if (!currentUser) return false;
    
    try {
      console.log('=== SAVING CRM SELECTION TO LEAD ===');
      console.log('User email:', currentUser.email);
      console.log('CRM Selection:', crmSelection);
      
      // The lead account this login is linked to
      let accountsSnapshot = await getMyAccountSnapshot('Leads');
      
      if (accountsSnapshot.empty) {
        console.log('No lead account found for email:', currentUser.email);
        return false;
      }

      const leadDoc = accountsSnapshot.docs[0];
      const leadId = leadDoc.id;
      console.log('Found lead account:', leadId);
      
      // Prepare CRM data for lead document
      const crmData = {
        [`techData.${crmSelection.toolId || 'crm'}`]: crmSelection.name,
        [`techData.${crmSelection.toolId || 'crm'}Other`]: crmSelection.name === 'Other' ? crmSelection.customName : null,
        [`techData.${crmSelection.toolId || 'crm'}SelectedAt`]: crmSelection.selectedAt,
        [`techData.${crmSelection.toolId || 'crm'}Completed`]: true,
        
        // Also save with generic crmSoftware field for compatibility
        'techData.crmSoftware': crmSelection.name,
        'techData.crmSoftwareOther': crmSelection.name === 'Other' ? crmSelection.customName : null,
        'techData.crmSelectedAt': crmSelection.selectedAt,
        'techData.crmCompleted': true,
        
        lastUpdated: new Date().toISOString()
      };
      
      console.log('CRM data to save to lead:', crmData);
      
      // Update the lead document
      await updateDoc(doc(db, 'accounts', leadId), crmData);
      
      console.log('Successfully saved CRM selection to lead:', leadId);
      return true;
    } catch (error) {
      console.error('Error saving CRM selection to lead:', error);
      return false;
    }
  };

  // Handle save and continue
  const handleSaveAndContinue = async () => {
    try {
      // Save CRM software selection to Firestore
      if (currentUser) {
        console.log('=== SAVE DEBUG START ===');
        console.log('Current techStack.core.tools:', techStack.core?.tools);
        console.log('Current userTechStack:', userTechStack);
        
        // Find CRM software selection from userTechStack
        const crmSelection = userTechStack.find(item => 
          item.categoryId === 'core' && isCRMTool({ name: item.name, id: item.toolId })
        );
        
        console.log('CRM Selection from userTechStack:', crmSelection);
        
        // Also check the techStack state for current CRM selection
        const currentCRMFromState = techStack.core?.tools?.find(tool => 
          isCRMTool(tool) && tool.software && tool.software !== ''
        );
        
        console.log('Current CRM from techStack state:', currentCRMFromState);
        
        // Check all tools in core to see what we have
        console.log('All core tools:', techStack.core?.tools?.map(tool => ({
          id: tool.id,
          name: tool.name,
          software: tool.software,
          isCRM: isCRMTool(tool)
        })));
        
        // Use the most recent selection (prefer userTechStack, fallback to state)
        const finalCRMSelection = crmSelection || (currentCRMFromState ? {
          name: currentCRMFromState.software,
          customName: currentCRMFromState.customName,
          toolId: currentCRMFromState.id,
          selectedAt: new Date().toISOString()
        } : null);
        
        console.log('Final CRM selection to save:', finalCRMSelection);
        
        if (!finalCRMSelection) {
          console.error('No CRM selection found! Cannot save.');
          alert('No CRM selection found. Please select a CRM software first.');
          return;
        }
        
        const updateData = {
          // Save CRM selection to techData collection - using the actual tool ID
          [`techData.${finalCRMSelection.toolId || 'crm'}`]: finalCRMSelection.name,
          [`techData.${finalCRMSelection.toolId || 'crm'}Other`]: finalCRMSelection.name === 'Other' ? finalCRMSelection.customName : null,
          [`techData.${finalCRMSelection.toolId || 'crm'}SelectedAt`]: finalCRMSelection.selectedAt,
          [`techData.${finalCRMSelection.toolId || 'crm'}Completed`]: true,
          
          // Also save with generic crmSoftware field for compatibility
          'techData.crmSoftware': finalCRMSelection.name,
          'techData.crmSoftwareOther': finalCRMSelection.name === 'Other' ? finalCRMSelection.customName : null,
          'techData.crmSelectedAt': finalCRMSelection.selectedAt,
          'techData.crmCompleted': true,
          
          // Save to businessAssessment section (for existing conditions tracking)
          'businessAssessment.techStackCompleted': true,
          'businessAssessment.techStackData': techStack,
          
          // Save to existingConditionsProgress for completion tracking
          'existingConditionsProgress.techStackCompleted': true,
          
          // Save the selected software data
          'techStack.selectedSoftware': userTechStack,
          'techStack.lastUpdated': new Date().toISOString(),
          
          lastUpdated: new Date().toISOString()
        };
        
        console.log('Update data to be saved:', updateData);
        
        await updateDoc(doc(db, 'users', currentUser.uid), updateData);
        
        console.log('CRM data saved successfully to user document');
        
        // Also save CRM selection to leads collection
        try {
          console.log('Saving CRM selection to leads collection...');
          const leadSaveSuccess = await saveCRMSelectionToLead(finalCRMSelection);
          
          if (leadSaveSuccess) {
            console.log('Successfully saved CRM selection to leads collection');
          } else {
            console.log('No lead account found or failed to save CRM selection to leads');
          }
        } catch (leadSaveError) {
          console.error('Error saving CRM selection to leads:', leadSaveError);
          // Continue even if lead save fails
        }
        
        console.log('=== SAVE DEBUG END ===');
      }
      
      // Navigate back to existing conditions page
      navigate('/start/existing-conditions', { state: { fromCrmTechStack: true } });
    } catch (error) {
      console.error('Error saving CRM selection:', error);
      alert('Error saving data. Please try again.');
    }
  };

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative">
      <StartSidebar />
      <div className="w-full p-6">
        <div className="max-w-6xl mx-auto">
        <div className="flex justify-center items-center mb-6 relative">
          <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Tech Stack</h1>
          <div 
            className="absolute right-0 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center"
          >
            <FaLock className="mr-2 text-sm" />
            All Software
          </div>
        </div>

        {/* Continue Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={handleSaveAndContinue}
            disabled={!Object.values(techStack).some(category => 
              category.tools.some(tool => isCRMTool(tool) && tool.software && tool.software !== '')
            )}
            className={`px-6 py-3 rounded-lg transition-colors font-medium ${
              // Check if any CRM tool has software selected
              Object.values(techStack).some(category => 
                category.tools.some(tool => isCRMTool(tool) && tool.software && tool.software !== '')
              )
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue
          </button>
        </div>
        
        {/* Cost Summary */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 mb-6`}>
          {/* Toggle Button */}
          <div className="flex justify-center mb-4">
            <div 
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm flex items-center"
            >
              <FaLock className="mr-2 text-sm" />
              {showDetailedCosts ? 'Hide Details' : 'Show Details'}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4 text-center`}>
              <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total Monthly Cost</h2>
              <div className="flex items-center justify-center mt-2">
                <FaLock className={`text-2xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </div>
            <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4 text-center`}>
              <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total Annual Cost</h2>
              <div className="flex items-center justify-center mt-2">
                <FaLock className={`text-2xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </div>
            <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4 text-center`}>
              <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Integration</h2>
              <div className="flex items-center justify-center mt-2">
                <FaLock className={`text-2xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </div>
          </div>
          
          {/* Component Cost Breakdown - Only shown when showDetailedCosts is true */}
          {showDetailedCosts && (
            <>
              <h3 className={`text-md font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 text-center`}>Cost Breakdown by Component</h3>
              <div className="grid grid-cols-3 gap-4">
                {orderedCategories.map(categoryId => (
                  <div key={`cost-${categoryId}`} className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3`}>
                    <div className="flex flex-col items-center">
                      <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{techStack[categoryId].title}</h4>
                      <span className="text-sm font-medium text-red-400 mt-1">{costs.components[categoryId].percentOfTotal.toFixed(1)}% of total</span>
                    </div>
                    <div className="mt-2 text-center">
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Monthly: <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>${costs.components[categoryId].monthly.toFixed(2)}</span></div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Annual: <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>${costs.components[categoryId].annual.toFixed(2)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        
        {/* Instructions */}
        <div className="flex justify-center mb-4">
          <div className={`bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 border rounded-xl p-3 max-w-md`}>
            <p className={`text-sm text-center ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
              💡 <strong>Click on the CRM circle below in Core</strong> to select your CRM software
            </p>
          </div>
        </div>

        {/* Toggle Button and Legend */}
        <div className="flex flex-col items-center mb-4 space-y-3">
          <div className="flex space-x-3">
            <div 
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm flex items-center"
            >
              <FaLock className="mr-2 text-sm" />
              {showPercentages ? 'Show Apps' : 'Show Cost %'}
            </div>
            <div 
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center"
            >
              <FaLock className="mr-2 text-sm" />
              Check Duplicates
            </div>
          </div>
          
          {/* API Status Legend */}
          <div className={`flex items-center space-x-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-gray-100"></div>
              <span>Open API Available</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative group">
                <svg 
                  className="w-4 h-4 text-blue-500 hover:text-blue-700 cursor-help transition-colors" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 ${
                  darkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-white text-gray-900 border border-gray-200'
                }`}>
                  Open API allows different software applications to communicate and share data automatically. This enables integration between your tools, reducing manual work and improving efficiency.
                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                    darkMode ? 'border-t-gray-800' : 'border-t-white'
                  }`}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tech Stack Pyramid */}
        <div className="flex flex-col items-center space-y-4 mb-12">
          {orderedCategories.map((categoryId, index) => {
            const category = techStack[categoryId];
            const delay = 0.1 + index * 0.15;
            
            return (
              <motion.div
                key={category.id}
                className="w-full flex justify-center"
                initial={{ y: -100, opacity: 0 }}
                animate={isLoaded ? { y: 0, opacity: 1 } : { y: -100, opacity: 0 }}
                transition={{ 
                  duration: 0.5, 
                  delay: delay, 
                  type: "spring", 
                  stiffness: 100, 
                  damping: 15 
                }}
              >
                <motion.div 
                  className={`${category.color} rounded-lg shadow-lg p-3`}
                  style={{ width: category.width }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex justify-center items-center mb-3">
                    <h2 className="text-xl font-bold text-white text-center">{category.title}</h2>
                  </div>
                  
                  <div className="flex flex-wrap justify-center gap-3">
                    {category.tools.map(tool => {
                      const isCRM = isCRMTool(tool);
                      return (
                        <div key={tool.id} className="flex flex-col items-center">
                          <div 
                            className={`w-10 h-10 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'} rounded-full flex items-center justify-center transition-colors shadow-sm border-2 overflow-hidden ${
                              isCRM 
                                ? `cursor-pointer hover:scale-110 hover:shadow-lg border-blue-500 ${
                                    !tool.software || tool.software === '' 
                                      ? 'animate-pulse ring-2 ring-blue-400 ring-opacity-50' 
                                      : ''
                                  }` 
                                : ''
                            } ${
                              tool.software && tool.availableSoftware ? 
                                (() => {
                                  const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
                                  return selectedSoftware?.open ? 'border-green-500' : 'border-transparent';
                                })() 
                                : 'border-transparent'
                            }`}
                            onClick={(e) => isCRM && handleCRMToolClick(e, tool, categoryId)}
                          >
                            {showPercentages ? (
                              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xs font-medium`}>
                                {tool.percentOfComponent ? Math.round(tool.percentOfComponent) + '%' : '0%'}
                              </span>
                            ) : isCRM ? (
                              tool.software === 'None' ? (
                                <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} text-xl font-medium`}>✗</span>
                              ) : tool.software && tool.availableSoftware ? (
                                (() => {
                                  const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
                                  const imageKey = `${tool.id}-${tool.software}`;
                                  const hasImageError = imageErrors.has(imageKey);
                                  
                                  if (hasImageError || !selectedSoftware?.image) {
                                    return (
                                      <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xl font-medium`}>?</span>
                                    );
                                  }
                                  
                                  return (
                                    <img
                                      src={selectedSoftware.image}
                                      alt={tool.software}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '';
                                        setImageErrors(prev => new Set([...prev, imageKey]));
                                      }}
                                    />
                                  );
                                })()
                              ) : (
                                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xl font-medium`}>?</span>
                              )
                            ) : (
                              <FaLock className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-lg`} />
                            )}
                          </div>
                          <h3 className="font-medium text-center text-xs text-white mt-1">{tool.name}</h3>
                          {isCRM && (!tool.software || tool.software === '') && (
                            <span className="text-xs text-blue-200 mt-0.5 animate-pulse">
                              Click to select
                            </span>
                          )}
                          {tool.software && (
                            <span className="text-xs text-red-100 mt-0.5">
                              {tool.software === 'Other' && tool.customName ? tool.customName : tool.software}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
        
      </div>

      {/* Tool Configuration Popup - Only for CRM tools */}
      {activeTool && activeToolData && isCRMTool(activeToolData) && (
        <motion.div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-hidden`}
            initial={{ scale: 0.5, rotateY: 90 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 0.4, type: "spring" }}
          >
            <div className={`${techStack[activeToolData.categoryId].color} p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex-1"></div>
                <h2 className="text-2xl font-bold text-white text-center flex-1">{activeToolData.name}</h2>
                <button 
                  onClick={handleClosePopup}
                  className="text-white hover:text-gray-200 flex-1 flex justify-end"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              {/* Tabs */}
              <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-6`}>
                <nav className="-mb-px flex justify-center space-x-8">
                  <button
                    className={`border-b-2 px-1 py-4 text-sm font-medium ${
                      activeTab === 'select' 
                        ? 'border-red-500 text-red-400' 
                        : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                    }`}
                    onClick={() => setActiveTab('select')}
                  >
                    Select
                  </button>
                  <button
                    className={`border-b-2 px-1 py-4 text-sm font-medium ${
                      activeTab === 'about' 
                        ? 'border-red-500 text-red-400' 
                        : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                    }`}
                    onClick={() => setActiveTab('about')}
                  >
                    About
                  </button>
                </nav>
              </div>

              {/* Select Tab */}
              {activeTab === 'select' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    {/* None option */}
                    <div 
                      className={`border rounded-lg p-4 ${
                        activeToolData.software === 'None' 
                          ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50') 
                          : (darkMode ? 'border-gray-600' : 'border-gray-200')
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 w-12 h-12">
                            <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                              <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} text-xl`}>✗</span>
                            </div>
                          </div>
                          <div>
                            <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              None
                            </h3>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={async () => {
                              const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                              const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                tool => tool.id === activeToolData.id
                              );
                              if (toolIndex !== -1) {
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = 'None';
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost = 0;
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost = 0;
                                setTechStack(updatedTechStack);
                                
                                // Save to user's tech stack
                                if (currentUser) {
                                  const noneSoftwareData = {
                                    id: 'none',
                                    name: 'None',
                                    categoryId: activeToolData.categoryId,
                                    toolId: activeToolData.id,
                                    monthlyCost: 0,
                                    annualCost: 0,
                                    selectedAt: new Date().toISOString()
                                  };
                                  
                                  const updatedUserTechStack = [...userTechStack.filter(item => 
                                    !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                  ), noneSoftwareData];
                                  
                                  console.log('Saving None selection:', noneSoftwareData);
                                  setUserTechStack(updatedUserTechStack);
                                  updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                  
                                  // If this is a CRM tool, also save to leads collection immediately
                                  if (isCRMTool(activeToolData)) {
                                    try {
                                      console.log('Saving CRM None selection to leads collection immediately...');
                                      const leadSaveSuccess = await saveCRMSelectionToLead(noneSoftwareData);
                                      
                                      if (leadSaveSuccess) {
                                        console.log('Successfully saved CRM None selection to leads collection immediately');
                                      } else {
                                        console.log('No lead account found or failed to save CRM None selection to leads immediately');
                                      }
                                    } catch (leadSaveError) {
                                      console.error('Error saving CRM None selection to leads immediately:', leadSaveError);
                                      // Continue even if lead save fails
                                    }
                                  }
                                }
                              }
                            }}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              activeToolData.software === 'None'
                                ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700')
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            {activeToolData.software === 'None' ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Other option */}
                    <div 
                      className={`border rounded-lg p-4 ${
                        activeToolData.software === 'Other' 
                          ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50') 
                          : (darkMode ? 'border-gray-600' : 'border-gray-200')
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 w-12 h-12">
                            <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-xl`}>+</span>
                            </div>
                          </div>
                          <div>
                            <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              Other
                            </h3>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Specify your own CRM software
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={async () => {
                              const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                              const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                tool => tool.id === activeToolData.id
                              );
                              if (toolIndex !== -1) {
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = 'Other';
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost = 0;
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost = 0;
                                setTechStack(updatedTechStack);
                                setShowCustomInput(true);
                                
                                // Save to user's tech stack
                                if (currentUser) {
                                  const otherSoftwareData = {
                                    id: 'other',
                                    name: 'Other',
                                    categoryId: activeToolData.categoryId,
                                    toolId: activeToolData.id,
                                    monthlyCost: 0,
                                    annualCost: 0,
                                    customName: customCRMName,
                                    selectedAt: new Date().toISOString()
                                  };
                                  
                                  const updatedUserTechStack = [...userTechStack.filter(item => 
                                    !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                  ), otherSoftwareData];
                                  
                                  console.log('Saving Other selection:', otherSoftwareData);
                                  setUserTechStack(updatedUserTechStack);
                                  updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                  
                                  // If this is a CRM tool, also save to leads collection immediately
                                  if (isCRMTool(activeToolData)) {
                                    try {
                                      console.log('Saving CRM Other selection to leads collection immediately...');
                                      const leadSaveSuccess = await saveCRMSelectionToLead(otherSoftwareData);
                                      
                                      if (leadSaveSuccess) {
                                        console.log('Successfully saved CRM Other selection to leads collection immediately');
                                      } else {
                                        console.log('No lead account found or failed to save CRM Other selection to leads immediately');
                                      }
                                    } catch (leadSaveError) {
                                      console.error('Error saving CRM Other selection to leads immediately:', leadSaveError);
                                      // Continue even if lead save fails
                                    }
                                  }
                                }
                              }
                            }}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              activeToolData.software === 'Other'
                                ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700')
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            {activeToolData.software === 'Other' ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      </div>
                      
                      {/* Custom name input - show when Other is selected */}
                      {activeToolData.software === 'Other' && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                            CRM Software Name
                          </label>
                          <input
                            type="text"
                            value={customCRMName}
                            onChange={async (e) => {
                              setCustomCRMName(e.target.value);
                              // Update the tool's custom name in real-time
                              const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                              const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                tool => tool.id === activeToolData.id
                              );
                              if (toolIndex !== -1) {
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].customName = e.target.value;
                                setTechStack(updatedTechStack);
                                
                                // Save to user's tech stack
                                if (currentUser) {
                                  const otherSoftwareData = {
                                    id: 'other',
                                    name: 'Other',
                                    categoryId: activeToolData.categoryId,
                                    toolId: activeToolData.id,
                                    monthlyCost: 0,
                                    annualCost: 0,
                                    customName: e.target.value,
                                    selectedAt: new Date().toISOString()
                                  };
                                  
                                  const updatedUserTechStack = [...userTechStack.filter(item => 
                                    !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                  ), otherSoftwareData];
                                  
                                  setUserTechStack(updatedUserTechStack);
                                  updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                  
                                  // If this is a CRM tool, also save to leads collection immediately
                                  if (isCRMTool(activeToolData)) {
                                    try {
                                      console.log('Saving CRM custom name update to leads collection immediately...');
                                      const leadSaveSuccess = await saveCRMSelectionToLead(otherSoftwareData);
                                      
                                      if (leadSaveSuccess) {
                                        console.log('Successfully saved CRM custom name update to leads collection immediately');
                                      } else {
                                        console.log('No lead account found or failed to save CRM custom name update to leads immediately');
                                      }
                                    } catch (leadSaveError) {
                                      console.error('Error saving CRM custom name update to leads immediately:', leadSaveError);
                                      // Continue even if lead save fails
                                    }
                                  }
                                }
                              }
                            }}
                            placeholder="Enter your CRM software name"
                            className={`block w-full rounded-md border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'} shadow-sm p-2 focus:ring-red-500 focus:border-red-500`}
                          />
                        </div>
                      )}
                    </div>
                    
                    {activeToolData.availableSoftware?.map((software) => (
                      <div 
                        key={software.id}
                        className={`border rounded-lg p-4 ${
                          software.name === activeToolData.software 
                            ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50') 
                            : (darkMode ? 'border-gray-600' : 'border-gray-200')
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 w-12 h-12">
                              {software.image ? (
                                (() => {
                                  const imageKey = `popup-${software.id}`;
                                  const hasImageError = imageErrors.has(imageKey);
                                  
                                  if (hasImageError) {
                                    return (
                                      <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-xl`}>?</span>
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                    <img 
                                      src={software.image} 
                                      alt={software.name}
                                      className="w-full h-full object-cover rounded-lg"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '';
                                        setImageErrors(prev => new Set([...prev, imageKey]));
                                      }}
                                    />
                                  );
                                })()
                              ) : (
                                <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                                  <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-xl`}>?</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {software.name}
                                {software.suggested && (
                                  <span className="ml-2 text-red-400">★ Suggested</span>
                                )}
                              </h3>
                              {software.open && (
                                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                  Open API
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={async () => {
                                const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                                const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                  tool => tool.id === activeToolData.id
                                );
                                if (toolIndex !== -1) {
                                  updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = software.name;
                                  if (software.pricing && software.pricing[0]) {
                                    let monthlyCost = 0;
                                    const plan = software.pricing[0];
                                    
                                    if (plan.tieredPricing && plan.tiers && plan.tiers.length > 0) {
                                      // For tiered pricing, use the base cost from the first tier
                                      monthlyCost = parseFloat(plan.tiers[0]?.baseMonthlyCost) || 0;
                                    } else if (plan.variablePricing) {
                                      // For variable pricing, use the monthly cost (user-entered)
                                      monthlyCost = parseFloat(plan.monthlyCost) || 0;
                                    } else {
                                      // For simple pricing, use the monthly cost
                                      monthlyCost = parseFloat(plan.monthlyCost) || 0;
                                    }
                                    
                                    updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost = monthlyCost;
                                    updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost = monthlyCost * 12;
                                  }
                                  setTechStack(updatedTechStack);
                                  
                                  // Save to user's tech stack
                                  if (currentUser) {
                                    const selectedSoftwareData = {
                                      ...software,
                                      categoryId: activeToolData.categoryId,
                                      toolId: activeToolData.id,
                                      monthlyCost: updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost,
                                      annualCost: updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost,
                                      selectedAt: new Date().toISOString()
                                    };
                                    
                                    const updatedUserTechStack = [...userTechStack.filter(item => 
                                      !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                    ), selectedSoftwareData];
                                    
                                    console.log('Saving software selection:', selectedSoftwareData);
                                    setUserTechStack(updatedUserTechStack);
                                    updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                    
                                    // If this is a CRM tool, also save to leads collection immediately
                                    if (isCRMTool(activeToolData)) {
                                      try {
                                        console.log('Saving CRM selection to leads collection immediately...');
                                        const leadSaveSuccess = await saveCRMSelectionToLead(selectedSoftwareData);
                                        
                                        if (leadSaveSuccess) {
                                          console.log('Successfully saved CRM selection to leads collection immediately');
                                        } else {
                                          console.log('No lead account found or failed to save CRM selection to leads immediately');
                                        }
                                      } catch (leadSaveError) {
                                        console.error('Error saving CRM selection to leads immediately:', leadSaveError);
                                        // Continue even if lead save fails
                                      }
                                    }
                                  }
                                }
                              }}
                              className={`px-3 py-1 rounded-md text-sm font-medium ${
                                software.name === activeToolData.software
                                  ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700')
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                            >
                              {software.name === activeToolData.software ? 'Selected' : 'Select'}
                            </button>
                            {software.signupUrl && (
                              <a
                                href={software.signupUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                              >
                                Sign Up
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* About Tab */}
              {activeTab === 'about' && (
                <div className="space-y-6">
                  <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-6`}>
                    <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>About {activeToolData.name}</h3>
                    <div className="prose prose-sm max-w-none">
                      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {activeToolData.description || 'No description available for this type.'}
                      </p>
                    </div>
                  </div>

                  <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-6`}>
                    <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Use Cases</h3>
                    <div className="prose prose-sm max-w-none">
                      {activeToolData.useCases && activeToolData.useCases.length > 0 ? (
                        <ul className={`list-disc pl-5 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {activeToolData.useCases.map((useCase, index) => (
                            <li key={index}>{useCase}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          No use cases available for this type.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t`}>
              <div className="flex justify-between items-center">
                <button 
                  onClick={handleClosePopup}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleClosePopup}
                  disabled={!activeToolData.software || (activeToolData.software === 'Other' && !customCRMName.trim())}
                  className={`px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeToolData.software && !(activeToolData.software === 'Other' && !customCRMName.trim())
                      ? 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </div>
      </div>
    </div>
  );
};

export default CRMtechstack;
